/** @jsxImportSource @emotion/react */
import { useEffect, useState } from 'react'
import styled from '@emotion/styled'
import { Dialog, DialogContent, Checkbox, CircularProgress } from '@material-ui/core'
import { StyledDialogTitle } from 'components/legacy-poorly-defined/Common'
import { ActionButton, StyledDialogActions } from 'components/widgets'
import { BRAND_COLOR, getAppColors } from 'lib/constants'
import { trans, TranslationGroup } from 'lib/types'
import { DiscoveredDeviceType } from 'lib/types/bluetoothPairing'
import { ScanStatus } from 'hooks/bluetoothPairing/useBluetoothScanStream'

const StatusRow = styled.div({
  display: 'flex',
  alignItems: 'center',
  gap: '0.8rem',
  minHeight: '2.4rem',
  marginBottom: '0.8rem',
  fontFamily: 'Montserrat',
  fontSize: '1.3rem',
})

const Hint = styled.span({ color: getAppColors('--color-secondary') })
const ErrorText = styled.span({ color: '#c0392b' })

const List = styled.div({
  display: 'flex',
  flexDirection: 'column',
})

const Row = styled.label<{ disabled?: boolean }>(({ disabled }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '0.4rem',
  padding: '0.4rem 0',
  borderBottom: `1px solid ${getAppColors('--color-gray-4')}`,
  cursor: disabled ? 'default' : 'pointer',
  opacity: disabled ? 0.45 : 1,
  fontFamily: 'Montserrat',
  fontSize: '1.3rem',
}))

const BrandCheckbox = styled(Checkbox)({
  // Leave the unchecked outline at the default tone; only tint the checkmark.
  '&.Mui-checked': { color: BRAND_COLOR },
})

const TitleRow = styled.span({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '1.2rem',
})

const Name = styled.span({ flex: 1, minWidth: 0 })
const Count = styled.span({ color: '#999', whiteSpace: 'nowrap' })

const Empty = styled.div({
  padding: '2.4rem 1.2rem',
  textAlign: 'center',
  color: '#999',
  fontStyle: 'italic',
})

type Props = {
  open: boolean
  status: ScanStatus
  message?: string
  error?: string
  discoveries: DiscoveredDeviceType[]
  knownNames: Set<string>
  onAdd: (names: string[]) => void
  onClose: () => void
}

export const AddDeviceTypesModal = ({
  open,
  status,
  message,
  error,
  discoveries,
  knownNames,
  onAdd,
  onClose,
}: Props) => {
  const settings: TranslationGroup = trans.settings()
  const common: TranslationGroup = trans.common()
  const [selected, setSelected] = useState<Set<string>>(new Set())

  // Reset the selection each time the modal is (re)opened.
  useEffect(() => {
    if (open) setSelected(new Set())
  }, [open])

  const toggle = (name: string) => {
    setSelected(prev => {
      const next = new Set(prev)
      if (next.has(name)) next.delete(name)
      else next.add(name)
      return next
    })
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <StyledDialogTitle>
        <TitleRow>
          {settings.discovered_devices}
          {(status === 'connecting' || status === 'connected') && <CircularProgress size={18} thickness={5} />}
        </TitleRow>
      </StyledDialogTitle>
      <DialogContent>
        <StatusRow>
          {message && <Hint>{message}</Hint>}
          {error && status !== 'connected' && <ErrorText>{error}</ErrorText>}
        </StatusRow>
        {discoveries.length === 0 ? (
          <Empty>{settings.no_discovered_devices}</Empty>
        ) : (
          <List>
            {discoveries.map(d => {
              const known = knownNames.has(d.name)
              const checked = known || selected.has(d.name)
              return (
                <Row key={d.name} disabled={known}>
                  <BrandCheckbox size="small" checked={checked} disabled={known} onChange={() => toggle(d.name)} />
                  <Name>{d.name}</Name>
                  <Count>{known ? settings.device_added : d.deviceCount}</Count>
                </Row>
              )
            })}
          </List>
        )}
      </DialogContent>
      <StyledDialogActions>
        <ActionButton width="110px" height="28px" onClick={onClose}>
          {common.cancel}
        </ActionButton>
        <ActionButton
          width="160px"
          height="28px"
          disabled={selected.size === 0}
          onClick={() => onAdd(Array.from(selected))}
        >
          {settings.add_selections}
        </ActionButton>
      </StyledDialogActions>
    </Dialog>
  )
}
